<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'

import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import { useIncidentSubmissionStore } from '../../stores/incident-submission'
import { useFileUploadStore } from '../../stores/file-upload'
import { regionConfig } from '@/config/region'

const incidentStore = useIncidentSubmissionStore()
const fileUploadStore = useFileUploadStore()

const { incidentForm } = storeToRefs(incidentStore)
const { primaryFile } = storeToRefs(fileUploadStore)

// --- Map Logic ---
const mapContainer = ref<HTMLElement | null>(null)
let mapInstance: L.Map | null = null
let mapResizeObserver: ResizeObserver | null = null

function initMap() {
  if (!mapContainer.value) return

  // Details only lets a report through once it has coordinates; never invent a pin if it somehow has none.
  const startLat = incidentForm.value.localLatitude
  const startLng = incidentForm.value.localLongitude
  if (startLat === null || startLng === null) {
    incidentStore.jumpToStep('incident-details')
    return
  }

  // Light theme map tiles to make our Safety Orange pin pop (Carto Light)
  const streetLayer = new L.TileLayer(
    'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    {
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      maxZoom: 20
    }
  )

  // Esri World Imagery is the most reliable free satellite tile provider
  // FIX: Changed from L.tileLayer to new L.TileLayer
  const satelliteLayer = new L.TileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution:
        'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 20
    }
  )

  // 2. Initialize the Map with the default layer (Street)
  mapInstance = new L.Map(mapContainer.value, {
    center: [startLat, startLng],
    zoom: 16,
    zoomControl: false, // Keeps UI clean; users can pinch/scroll
    layers: [satelliteLayer]
  })

  mapInstance.setMaxBounds([
    [regionConfig.mapBounds.sw.lat, regionConfig.mapBounds.sw.lng],
    [regionConfig.mapBounds.ne.lat, regionConfig.mapBounds.ne.lng]
  ])

  // 3. Add the Layer Toggle Control to the map
  const baseMaps = {
    'Street Map': streetLayer,
    Satellite: satelliteLayer
  }

  // FIX: Changed from L.control.layers to new L.Control.Layers
  new L.Control.Layers(baseMaps, undefined, {
    position: 'topright'
  }).addTo(mapInstance)

  // Update store when user stops panning
  mapInstance.on('moveend', () => {
    const center = mapInstance?.getCenter()
    if (center) {
      incidentForm.value.localLatitude = Number(center.lat.toFixed(5))
      incidentForm.value.localLongitude = Number(center.lng.toFixed(5))
      incidentStore.updateGeoJsonLocation()
    }
  })
}

onMounted(() => {
  initMap()

  if (mapContainer.value) {
    mapResizeObserver = new ResizeObserver(() => {
      if (!mapInstance) return
      mapInstance.invalidateSize()
      const lat = incidentForm.value.localLatitude
      const lng = incidentForm.value.localLongitude
      if (lat && lng) {
        mapInstance.setView([lat, lng], mapInstance.getZoom(), { animate: false })
      }
    })
    mapResizeObserver.observe(mapContainer.value)
  }
})

onBeforeUnmount(() => {
  mapResizeObserver?.disconnect()
  if (mapInstance) {
    mapInstance.remove()
  }
})

// --- Media Trimming Logic ---
const videoElement = ref<HTMLVideoElement | null>(null)
const videoDuration = ref(100) // Default until metadata loads
const trimRange = ref({ min: 0, max: 100 })
let lastTrimMin = 0
let lastTrimMax = 100

// Dynamically generate the preview URL for the primary video
const primaryVideoUrl = computed(() => {
  const file = primaryFile.value
  if (file?.localMediaUrl && file.uploadMimeType?.startsWith('video/')) {
    return file.localMediaUrl
  }
  return null
})

function onVideoMetadataLoaded() {
  if (videoElement.value) {
    const duration = Math.floor(videoElement.value.duration)
    videoDuration.value = duration
    trimRange.value = {
      min: Math.min(2, duration),
      max: Math.max(duration - 2, 0)
    }
    updateFileStoreTrims()
  }
}

function updateFileStoreTrims() {
  if (primaryFile.value) {
    fileUploadStore.updateClipRange(
      primaryFile.value.internalId,
      trimRange.value.min,
      trimRange.value.max
    )
  }
}

// Watch slider to push updates to API and seek the video player instantly
watch(
  trimRange,
  (newRange) => {
    updateFileStoreTrims()

    if (videoElement.value) {
      // If they dragged the 'Start' handle, seek to that frame
      if (newRange.min !== lastTrimMin) {
        videoElement.value.currentTime = newRange.min
        lastTrimMin = newRange.min
      }
      // If they dragged the 'End' handle, seek to that frame
      else if (newRange.max !== lastTrimMax) {
        videoElement.value.currentTime = newRange.max
        lastTrimMax = newRange.max
      }
    }
  },
  { deep: true }
)

// Trap the playback within the user's defined trim window
function enforceTrimBounds() {
  if (!videoElement.value) return

  const current = videoElement.value.currentTime

  // If video plays past the End marker, loop it back to the Start marker
  if (current >= trimRange.value.max) {
    videoElement.value.currentTime = trimRange.value.min
    videoElement.value.play().catch(() => {}) // Keep playing to create a seamless loop
  }

  // If they somehow click the native timeline before the Start marker, bump them forward
  if (current < trimRange.value.min) {
    videoElement.value.currentTime = trimRange.value.min
  }
}

// Watch slider to continuously push updates to the API marker DTOs
watch(
  trimRange,
  () => {
    updateFileStoreTrims()
  },
  { deep: true }
)
</script>

<template>
  <div class="row q-col-gutter-lg">
    <div class="col-12 col-md-6 flex column">
      <div>
        <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9">Refine location</h6>
        <p class="text-body2 text-blue-grey-8">Drag the map to pinpoint the exact location.</p>
      </div>

      <q-card flat bordered class="bg-blue-grey-1 border-deep-asphalt text-blue-grey-9 q-mb-sm">
        <q-card-section class="q-pa-sm row items-center">
          <div class="text-caption text-blue-grey-7">
            {{ incidentForm.address?.rawInput || 'No address provided' }}
          </div>
        </q-card-section>
      </q-card>

      <div
        class="map-wrapper relative-position rounded-borders overflow-hidden bg-blue-grey-1 border-flat"
        style="height: 420px"
      >
        <div ref="mapContainer" class="full-height full-width"></div>

        <div
          class="pointer-events-none center-pin-container flex flex-center"
          style="position: absolute; left: 50%; top: 50%; transform: translate(-50%, -100%)"
        >
          <q-icon name="mdi-map-marker" color="blue-8" size="50px" class="pin-drop-shadow" />
          <q-icon
            name="mdi-record-circle-outline"
            color="white"
            size="16px"
            class="absolute-center"
            style="margin-top: -6px"
          />
        </div>
      </div>
      <div class="text-caption text-weight-bold text-blue-grey-6 q-mt-sm text-center">
        LAT: {{ incidentForm.localLatitude || '---' }}, LONG:
        {{ incidentForm.localLongitude || '---' }}
      </div>
    </div>

    <div class="col-12 col-md-6 flex column">
      <div>
        <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9">Trim footage</h6>
        <p class="text-body2 text-blue-grey-8">
          Trim the video to the start and end of the incident.
        </p>
      </div>

      <q-card flat bordered class="bg-black rounded-borders overflow-hidden q-mb-md">
        <div v-if="primaryVideoUrl" class="video-container relative-position">
          <video
            ref="videoElement"
            :src="primaryVideoUrl"
            controls
            class="full-width block"
            style="height: 250px; object-fit: contain; background-color: #000"
            @loadedmetadata="onVideoMetadataLoaded"
            @timeupdate="enforceTrimBounds"
          ></video>
        </div>

        <div v-else class="flex flex-center bg-blue-grey-9 text-blue-grey-4" style="height: 250px">
          <div class="text-center">
            <q-icon name="mdi-video-off" size="xl" />
            <div class="text-subtitle2 q-mt-sm">No primary video selected</div>
            <div class="text-caption">Select a video in Step 2 to enable clipping.</div>
          </div>
        </div>
      </q-card>

      <div
        v-if="primaryVideoUrl"
        class="q-px-md q-pb-md border-flat rounded-borders bg-white q-mb-md"
      >
        <div
          class="row justify-between text-caption text-weight-bold text-blue-grey-8 q-mb-sm q-pt-sm"
        >
          <span>Start: {{ trimRange.min }}s</span>
          <span>End: {{ trimRange.max }}s</span>
        </div>
        <q-range
          v-model="trimRange"
          :min="0"
          :max="videoDuration"
          color="blue-8"
          track-color="blue-grey-2"
          inner-track-color="blue-3"
          thumb-color="blue-8"
          drag-range
          label-always
          :left-label-value="`${trimRange.min}s`"
          :right-label-value="`${trimRange.max}s`"
        />
        <div class="text-center text-caption text-blue-grey-5 q-mt-xs">
          Highlight Duration: {{ trimRange.max - trimRange.min }} seconds
        </div>
      </div>

      <q-space />
    </div>
  </div>
</template>

<style scoped>
.border-flat {
  border: 1px solid var(--q-blue-grey-3);
}

.border-deep-asphalt {
  border-color: var(--q-blue-grey-9) !important;
}

/* Ensure the pin sits clearly above the Leaflet map tiles (z-index usually 400 for panes) */
.center-pin-container {
  z-index: 500;
}

.pin-drop-shadow {
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.4));
}
</style>
